import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Plus, Trash, PencilSimple, FilePdf, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printPdf as printPdfUtil } from '../utils/pdfUtils';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BuildingStatement() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Phase 212 — Tabbed navigation: Invoices / Payments / Statements
  // (mirrors the apartment page structure). The Statements tab owns the
  // date filters, summary cards, unified ledger and "View PDF" action;
  // Invoices/Payments tabs each focus on their own management workflow.
  const [activeTab, setActiveTab] = useState('invoices');
  const TABS = ['invoices', 'payments', 'statements'];

  // Payment state
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  // Phase 220 — Building↔Finance bidirectional sync. Default sync ON; user
  // selects bank account when paying via bank so the matching Finance
  // income lands on the right bank ledger.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payForm, setPayForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '', notes: '',
    payment_source: 'bank',
    bank_account_id: '',
    sync_to_finance: true,
  });
  const [deletePayId, setDeletePayId] = useState(null);

  // Invoice state
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invForm, setInvForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', description: '', invoice_type: 'cleaning', notes: '' });
  const [deleteInvTarget, setDeleteInvTarget] = useState(null); // { invoice_id, source }

  // Phase 209y — Edit Building dialog state. Available for ALL building
  // types so cleaning_only buildings (which land on this page) can also
  // edit name / address / contact / notes / building_type.
  // Phase 211 — Now also includes monthly billing/pricing (cleaning_fee,
  // cleaning_vat_rate, cleaning_charge_mode) per user request.
  const [editBldgDialogOpen, setEditBldgDialogOpen] = useState(false);
  const [editBldgForm, setEditBldgForm] = useState({
    name: '', street_address: '', zip_code: '', city: '', country: '',
    building_type: 'managed', notes: '', managers: [],
    cleaning_fee: 0, cleaning_vat_rate: 19, cleaning_charge_mode: 'per_cleaning',
  });

  const fmtDate = (d) => {
    if (!d) return '—';
    const p = d.split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
  };

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.append('date_from', dateFrom);
      if (dateTo) p.append('date_to', dateTo);
      const res = await axios.get(`${API}/buildings/${buildingId}/statement?${p}`, { withCredentials: true });
      setStatement(res.data);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, dateFrom, dateTo, t]);

  useEffect(() => { fetchStatement(); }, [fetchStatement]);

  // Phase 220 — fetch bank accounts so the Pay dialog can route the
  // payment to a specific account; also lets the badge-aware row know
  // which bank to show in the audit trail.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/bank-accounts`, { withCredentials: true });
        setBankAccounts(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Phase 209y — Edit Building handlers
  const openEditBuildingDialog = async () => {
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}`, { withCredentials: true });
      const b = r.data || {};
      setEditBldgForm({
        name: b.name || '',
        street_address: b.street_address || b.address || '',
        zip_code: b.zip_code || '',
        city: b.city || '',
        country: b.country || '',
        building_type: b.building_type || 'managed',
        notes: b.notes || '',
        managers: b.managers || [],
        cleaning_fee: b.cleaning_fee ?? 0,
        cleaning_vat_rate: b.cleaning_vat_rate ?? 19,
        cleaning_charge_mode: b.cleaning_charge_mode || 'per_cleaning',
      });
      setEditBldgDialogOpen(true);
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const handleEditBldgSubmit = async (e) => {
    e.preventDefault();
    try {
      // Phase 211 — Use PATCH (partial update) so we don't accidentally
      // wipe fields like cleaning_fee/cleaning_vat_rate when the form
      // omits them; PATCH endpoint validates against `BuildingUpdate`
      // (all fields Optional) and supports street_address/zip_code/city.
      await axios.patch(`${API}/buildings/${buildingId}`, editBldgForm, { withCredentials: true });
      toast.success(t('savedSuccess'));
      setEditBldgDialogOpen(false);
      fetchStatement();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  // === PAYMENT HANDLERS ===
  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error(t('invalidAmount')); return; }
    if (payForm.payment_source === 'bank' && !payForm.bank_account_id) {
      toast.error(t('selectBankAccount') || 'Select a bank account'); return;
    }
    try {
      await axios.post(`${API}/buildings/${buildingId}/building-payments`, {
        date: payForm.date,
        amount: parseFloat(payForm.amount),
        notes: payForm.notes,
        payment_source: payForm.payment_source,
        bank_account_id: payForm.payment_source === 'bank' ? payForm.bank_account_id : null,
        sync_to_finance: payForm.sync_to_finance,
      }, { withCredentials: true });
      toast.success(t('savedSuccess'));
      setPayDialogOpen(false);
      setPayForm({
        date: new Date().toISOString().split('T')[0], amount: '', notes: '',
        payment_source: 'bank',
        bank_account_id: bankAccounts[0]?.account_id || '',
        sync_to_finance: true,
      });
      fetchStatement();
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleDeletePayment = async () => {
    if (!deletePayId) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/building-payments/${deletePayId}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeletePayId(null);
      fetchStatement();
    } catch { toast.error(t('errorOccurred')); }
  };

  // === INVOICE HANDLERS ===
  // Phase 211 — Add Invoice dialog now supports two modes:
  //   • Recurring: enables fixed_monthly mode and uses the saved building
  //     pricing to auto-generate monthly invoices going forward.
  //   • Manual: existing one-off invoice with custom amount/description.
  // The mode is held in `invMode` and only affects what the dialog shows
  // / what handleSaveInvoice does on submit.
  const [invMode, setInvMode] = useState('manual');

  const openAddInvoice = () => {
    setEditingInvoice(null);
    setInvMode('manual');
    setInvForm({ date: new Date().toISOString().split('T')[0], amount: '', description: '', invoice_type: 'cleaning', notes: '' });
    setInvDialogOpen(true);
  };

  const openEditInvoice = (entry) => {
    setEditingInvoice(entry);
    setInvForm({
      date: entry.date || '',
      amount: String(entry.amount || ''),
      description: entry.description || '',
      invoice_type: entry.invoice_type || 'other',
      notes: entry.notes || '',
    });
    setInvDialogOpen(true);
  };

  const handleSaveInvoice = async (e) => {
    e.preventDefault();

    // Phase 211 — RECURRING branch: switch the building's charge mode to
    // fixed_monthly so the existing auto-generation loop creates one
    // invoice per month from `takeover_date` onwards based on the saved
    // cleaning_fee + cleaning_vat_rate.
    if (!editingInvoice && invMode === 'recurring') {
      const fee = parseFloat(bld.cleaning_fee || 0);
      if (fee <= 0) {
        toast.error(t('setPricingFirst') || 'Set monthly cleaning charge in Edit Building first.');
        return;
      }
      try {
        await axios.patch(`${API}/buildings/${buildingId}`, {
          cleaning_charge_mode: 'fixed_monthly',
        }, { withCredentials: true });
        toast.success(t('recurringEnabled') || 'Recurring monthly invoicing enabled.');
        setInvDialogOpen(false);
        setEditingInvoice(null);
        fetchStatement();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('errorOccurred'));
      }
      return;
    }

    // Manual branch (existing behaviour)
    if (!invForm.amount || parseFloat(invForm.amount) <= 0) { toast.error(t('invalidAmount')); return; }

    try {
      if (editingInvoice) {
        // EDIT — for manual: full edit; for auto: only amount + description
        const payload = editingInvoice.source === 'auto'
          ? { amount: parseFloat(invForm.amount), description: invForm.description }
          : { date: invForm.date, amount: parseFloat(invForm.amount), description: invForm.description, invoice_type: invForm.invoice_type, notes: invForm.notes };

        await axios.put(`${API}/buildings/${buildingId}/invoices/${editingInvoice.invoice_id}`, payload, { withCredentials: true });
        toast.success(t('updatedSuccess'));
      } else {
        // CREATE new manual invoice
        await axios.post(`${API}/buildings/${buildingId}/invoices`, {
          date: invForm.date,
          amount: parseFloat(invForm.amount),
          description: invForm.description,
          invoice_type: invForm.invoice_type,
          notes: invForm.notes,
        }, { withCredentials: true });
        toast.success(t('savedSuccess'));
      }
      setInvDialogOpen(false);
      setEditingInvoice(null);
      fetchStatement();
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteInvTarget) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/invoices/${deleteInvTarget.invoice_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteInvTarget(null);
      fetchStatement();
    } catch { toast.error(t('errorOccurred')); }
  };

  // === HELPERS ===
  const sourceLabel = (source) => {
    if (source === 'auto') return { text: t('autoInvoice'), cls: 'border-blue-200 bg-blue-50 text-blue-700' };
    if (source === 'expense') return { text: t('expenseInvoice'), cls: 'border-purple-200 bg-purple-50 text-purple-700' };
    return { text: t('manualInvoice'), cls: 'border-orange-200 bg-orange-50 text-orange-700' };
  };

  const typeLabel = (type) => {
    if (type === 'cleaning') return t('cleaning');
    if (type === 'extra') return t('extraWork');
    return t('other');
  };

  const s = statement?.summary || {};
  const bld = statement?.building || {};
  const isPaid = (s.remaining_balance ?? 0) <= 0;
  const timeline = statement?.timeline || [];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-5" data-testid="building-statement-page">

        {/* === HEADER (Phase 212 — tab-only header; filters + summary live in the Statements tab) === */}
        <div className="bg-white border border-zinc-200" data-testid="statement-header">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-start gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/buildings')} className="rounded-none p-1 mt-0.5" data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="statement-title">
                {bld.name || '...'}
              </h1>
              {bld.address && (
                <p className="text-xs text-zinc-500 mt-0.5" data-testid="statement-building-address">
                  {bld.address}
                </p>
              )}
              {/* Phase 213 — Always-visible quick-reference info under the
                  address: primary contact + pricing summary. Auto-updates
                  from Edit Building since both come from the GET response. */}
              {(() => {
                const mgr = (bld.managers && bld.managers[0]) || {};
                const fee = parseFloat(bld.cleaning_fee || 0);
                const vat = parseFloat(bld.cleaning_vat_rate ?? 19);
                const total = fee * (1 + vat / 100);
                const modeLabel = bld.cleaning_charge_mode === 'fixed_monthly'
                  ? (t('fixedMonthly') || 'Monthly')
                  : (t('perCleaning') || 'Per Cleaning');
                return (
                  <div className="mt-2 space-y-1 text-[11px] text-zinc-600" data-testid="bldg-header-quick-info">
                    {(mgr.name || mgr.phone) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5" data-testid="bldg-header-contact">
                        {mgr.name && (
                          <span>
                            <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('contactPerson') || 'Contact Person'}:</span>
                            <span className="font-medium text-zinc-700">{mgr.name}</span>
                          </span>
                        )}
                        {mgr.phone && (
                          <span>
                            <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('phone') || 'Phone'}:</span>
                            <span className="font-mono text-zinc-700">{mgr.phone}</span>
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5" data-testid="bldg-header-pricing">
                      <span>
                        <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('chargeMode') || 'Charge Mode'}:</span>
                        <span className="font-medium text-zinc-700">{modeLabel}</span>
                      </span>
                      <span>
                        <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('monthlyCharge') || 'Monthly Charge'}:</span>
                        <span className="font-mono text-zinc-700">€{fee.toFixed(2)}</span>
                        <span className="text-zinc-400 ml-1">{t('exVat') || 'ex VAT'}</span>
                      </span>
                      <span>
                        <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('vat') || 'VAT'}:</span>
                        <span className="font-mono text-zinc-700">{vat.toFixed(0)}%</span>
                      </span>
                      <span>
                        <span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('totalIncVat') || 'Total incl. VAT'}:</span>
                        <span className="font-mono font-semibold text-zinc-900">€{total.toFixed(2)}</span>
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditBuildingDialog()}
                className="rounded-none flex-shrink-0"
                data-testid="edit-building-btn"
              >
                <PencilSimple size={16} className="mr-1.5" />{t('editBuilding')}
              </Button>
            )}
          </div>

          {/* Phase 212 — Tabs (Invoices / Payments / Statements) */}
          <div className="border-b border-zinc-100 flex gap-0 overflow-x-auto" data-testid="bldg-tabs">
            {TABS.map(tab => {
              let badge = null;
              if (tab === 'invoices') {
                const c = timeline.filter(e => e.type === 'invoice' && !e.is_brought_forward).length;
                if (c > 0) badge = c;
              } else if (tab === 'payments') {
                const c = timeline.filter(e => e.type === 'payment' && !e.is_brought_forward).length;
                if (c > 0) badge = c;
              }
              const labelKey = tab === 'statements' ? 'statementsTab' : tab;
              const fallback = tab === 'invoices' ? 'Invoices' : tab === 'payments' ? 'Payments' : 'Statements';
              return (
                <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'}`}
                  data-testid={`bldg-tab-${tab}`}
                >
                  {t(labelKey) === labelKey ? fallback : t(labelKey)}
                  {badge && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-zinc-200 text-zinc-600 rounded-sm">{badge}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400">{t('loading')}...</div>
        ) : statement && (
          <>
            {/* ============================================================
                INVOICES TAB — charges only (no statement-level View PDF)
            ============================================================ */}
            {activeTab === 'invoices' && (
            <div className="bg-white border border-zinc-200" data-testid="statement-charges">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t('chargesSection')}</h3>
                  <span className="text-[10px] text-zinc-400">({timeline.filter(e => e.type === 'invoice').length})</span>
                </div>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={openAddInvoice} className="rounded-none h-7 text-xs" data-testid="add-invoice-btn">
                    <Plus size={14} className="mr-1" />{t('addInvoice')}
                  </Button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b text-[11px] text-zinc-400 uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 w-28">{t('date')}</th>
                    <th className="text-left px-4 py-2.5 w-20">{t('source')}</th>
                    <th className="text-left px-4 py-2.5">{t('description')}</th>
                    <th className="text-right px-4 py-2.5 w-28">{t('amount')}</th>
                    {isAdmin && <th className="w-20"></th>}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.filter(e => e.type === 'invoice').length === 0 ? (
                    <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-8 text-zinc-300">{t('noData')}</td></tr>
                  ) : timeline.filter(e => e.type === 'invoice').map((entry, i) => {
                    const desc = language === 'el' && entry.description_el ? entry.description_el : entry.description;
                    const src = sourceLabel(entry.source);
                    const isBF = !!entry.is_brought_forward;
                    const canEdit = isAdmin && entry.invoice_id && !isBF;
                    const canDelete = isAdmin && entry.invoice_id && !isBF;
                    if (isBF) {
                      // Phase 210 — B/F row: special styling, no edit/delete,
                      // no inline PDF action. The carried-forward balance is
                      // an aggregate, not a real invoice.
                      return (
                        <tr key={`bf-${i}`} className="border-b border-zinc-100 bg-blue-50/40" data-testid="charge-bf-row">
                          <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{fmtDate(entry.date)}</td>
                          <td className="px-4 py-2.5">
                            <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase border border-blue-200 bg-blue-100 text-blue-700">B/F</span>
                          </td>
                          <td className="px-4 py-2.5 text-zinc-800 font-semibold">{desc}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-zinc-900">&euro;{entry.amount?.toFixed(2)}</td>
                          {isAdmin && <td></td>}
                          <td></td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={`inv-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors" data-testid={`charge-row-${i}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{fmtDate(entry.date)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase border ${src.cls}`}>{src.text}</span>
                        </td>
                        <td className="px-4 py-2.5 text-zinc-700">{desc}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold">&euro;{entry.amount?.toFixed(2)}</td>
                        {isAdmin && (
                          <td className="px-2 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {canEdit && (
                                <button onClick={() => openEditInvoice(entry)} className="text-zinc-300 hover:text-blue-600 transition-colors" data-testid={`edit-charge-${i}`}>
                                  <PencilSimple size={14} />
                                </button>
                              )}
                              {canDelete && (
                                <button onClick={() => setDeleteInvTarget({ invoice_id: entry.invoice_id, source: entry.source })} className="text-zinc-300 hover:text-red-500 transition-colors" data-testid={`delete-charge-${i}`}>
                                  <Trash size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-1 py-2.5 text-center">
                          <button
                            onClick={() => {
                              printPdfUtil(`/buildings/${buildingId}/statement/invoice-pdf`, {
                                invoice_id: entry.invoice_id || '',
                                date: entry.date || '',
                                description: desc || '',
                                amount: String(entry.amount || 0),
                                source: entry.source || 'manual',
                              });
                            }}
                            className="text-zinc-300 hover:text-blue-600 transition-colors"
                            title={t('view') || 'View'}
                            data-testid={`download-charge-${i}`}
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {timeline.filter(e => e.type === 'invoice').length > 0 && (
                  <tfoot>
                    <tr className="bg-zinc-50 border-t border-zinc-200">
                      <td colSpan={isAdmin ? 3 : 2} className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('totalCharges')}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-zinc-900">&euro;{s.total_charges?.toFixed(2)}</td>
                      {isAdmin && <td></td>}
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            )}

            {/* ============================================================
                PAYMENTS TAB — payments only (no statement-level View PDF)
            ============================================================ */}
            {activeTab === 'payments' && (
            <div className="bg-white border border-zinc-200" data-testid="statement-payments">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t('paymentsSection')}</h3>
                  <span className="text-[10px] text-zinc-400">({timeline.filter(e => e.type === 'payment').length})</span>
                </div>
                {isAdmin && (
                  <Button size="sm" onClick={() => setPayDialogOpen(true)} className="rounded-none h-7 bg-zinc-900 hover:bg-zinc-800 text-xs" data-testid="add-payment-btn">
                    <Plus size={14} className="mr-1" />{t('recordPayment')}
                  </Button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b text-[11px] text-zinc-400 uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 w-28">{t('date')}</th>
                    <th className="text-left px-4 py-2.5">{t('notes')}</th>
                    <th className="text-right px-4 py-2.5 w-28">{t('amount')}</th>
                    {isAdmin && <th className="w-16"></th>}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.filter(e => e.type === 'payment').length === 0 ? (
                    <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-zinc-300">{t('noPayments')}</td></tr>
                  ) : timeline.filter(e => e.type === 'payment').map((entry, i) => {
                    const isBF = !!entry.is_brought_forward;
                    const canDelete = isAdmin && entry.payment_id && !isBF;
                    if (isBF) {
                      // Phase 210 — B/F as a credit (carried-forward overpayment).
                      const desc = language === 'el' && entry.description_el ? entry.description_el : entry.description;
                      return (
                        <tr key={`bf-pay-${i}`} className="border-b border-zinc-100 bg-blue-50/40" data-testid="payment-bf-row">
                          <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{fmtDate(entry.date)}</td>
                          <td className="px-4 py-2.5 text-zinc-800 font-semibold">{desc}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">&euro;{entry.amount?.toFixed(2)}</td>
                          {isAdmin && <td></td>}
                          <td></td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={`pay-${i}`} className="border-b border-zinc-100 hover:bg-emerald-50/30 transition-colors" data-testid={`payment-row-${i}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{fmtDate(entry.date)}</td>
                        <td className="px-4 py-2.5 text-zinc-600">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{entry.notes || <span className="text-zinc-300">&mdash;</span>}</span>
                            {/* Phase 220 — "Synced with Finance" badge when this payment has a linked income row. */}
                            {entry.synced_income_id && (
                              <span
                                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200"
                                title={`${t('syncedWithFinance') || 'Synced with Finance'} · ${entry.synced_income_id}`}
                                data-testid={`payment-sync-badge-${i}`}
                              >
                                ⇄ {t('syncedWithFinance') || 'Synced with Finance'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">&euro;{entry.amount?.toFixed(2)}</td>
                        {isAdmin && (
                          <td className="px-2 py-2.5 text-center">
                            {canDelete && (
                              <button onClick={() => setDeletePayId(entry.payment_id)} className="text-zinc-300 hover:text-red-500 transition-colors" data-testid={`delete-pay-${i}`}>
                                <Trash size={14} />
                              </button>
                            )}
                          </td>
                        )}
                        <td className="px-1 py-2.5 text-center">
                          <button
                            onClick={() => {
                              printPdfUtil(`/buildings/${buildingId}/statement/receipt-pdf`, {
                                payment_id: entry.payment_id || '',
                                date: entry.date || '',
                                amount: String(entry.amount || 0),
                                notes: entry.notes || '',
                                remaining_balance: String(entry.running_balance || 0),
                              });
                            }}
                            className="text-zinc-300 hover:text-emerald-600 transition-colors"
                            title={t('view') || 'View'}
                            data-testid={`download-pay-${i}`}
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {timeline.filter(e => e.type === 'payment').length > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-50/50 border-t border-zinc-200">
                      <td className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('totalPayments')}</td>
                      <td></td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-emerald-700">&euro;{s.total_payments?.toFixed(2)}</td>
                      {isAdmin && <td></td>}
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            )}

            {/* ============================================================
                STATEMENTS TAB — date filters, summary, B/F-aware unified
                ledger and the single "View PDF" action. This tab is the
                ONLY place the full statement PDF can be opened, per user
                Phase 212 mandate.
            ============================================================ */}
            {activeTab === 'statements' && (
              <div className="space-y-4" data-testid="statement-tab">
                {/* Date filter + actions */}
                <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 bg-white border border-zinc-200 p-3" data-testid="stmt-date-filter">
                  <div>
                    <Label className="text-xs text-zinc-500">{t('fromDate')}</Label>
                    <DatePickerDMY value={dateFrom} onChange={setDateFrom} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="stmt-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('toDate')}</Label>
                    <DatePickerDMY value={dateTo} onChange={setDateTo} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="stmt-date-to" />
                  </div>
                  <Button onClick={fetchStatement} size="sm" className="rounded-none h-9 bg-zinc-900 hover:bg-zinc-800" data-testid="stmt-search-btn">{t('search')}</Button>
                  {(dateFrom || dateTo) && (
                    <Button type="button" variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); fetchStatement(); }} className="rounded-none h-9 text-xs" data-testid="stmt-clear-filter">
                      {t('clearFilter') || 'Clear'}
                    </Button>
                  )}
                  <div className="ml-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none h-9 text-xs gap-1.5"
                      data-testid="view-full-statement-pdf-btn"
                      onClick={() => {
                        const params = {};
                        if (dateFrom) params.date_from = dateFrom;
                        if (dateTo) params.date_to = dateTo;
                        printPdfUtil(`/buildings/${buildingId}/statement/full-pdf`, params);
                      }}
                    >
                      <FilePdf size={14} />{t('viewPdf') || 'View PDF'}
                    </Button>
                  </div>
                </div>

                {/* Summary metrics */}
                <div className="grid grid-cols-3 divide-x divide-zinc-200 bg-white border border-zinc-200">
                  <div className="px-5 py-4" data-testid="total-charges">
                    <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('totalCharges')}</span>
                    <span className="text-2xl font-bold font-mono">&euro;{s.total_charges?.toFixed(2)}</span>
                  </div>
                  <div className="px-5 py-4" data-testid="total-payments-summary">
                    <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('totalPayments')}</span>
                    <span className="text-2xl font-bold font-mono text-emerald-700">&euro;{s.total_payments?.toFixed(2)}</span>
                  </div>
                  <div className={`px-5 py-4 ${isPaid ? 'bg-emerald-50' : 'bg-red-50'}`} data-testid="remaining-balance">
                    <span className="text-[10px] uppercase block mb-1" style={{ color: isPaid ? '#047857' : '#b91c1c' }}>{t('remainingBalance')}</span>
                    <span className={`text-2xl font-bold font-mono ${isPaid ? 'text-emerald-700' : 'text-red-600'}`}>&euro;{s.remaining_balance?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Unified chronological ledger with running balance and B/F.
                    All transactions in date order — exactly what the PDF
                    statement shows. */}
                <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="stmt-ledger-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-zinc-50 border-b text-[11px] text-zinc-400 uppercase tracking-wider">
                        <th className="text-left px-4 py-2.5 w-28">{t('date')}</th>
                        <th className="text-left px-4 py-2.5">{t('description')}</th>
                        <th className="text-right px-4 py-2.5 w-28">{t('charges')}</th>
                        <th className="text-right px-4 py-2.5 w-28">{t('payments')}</th>
                        <th className="text-right px-4 py-2.5 w-28">{t('balance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-zinc-300">{t('noData')}</td></tr>
                      ) : timeline.map((entry, i) => {
                        const isBF = !!entry.is_brought_forward;
                        const isPay = entry.type === 'payment';
                        const desc = language === 'el' && entry.description_el ? entry.description_el : entry.description;
                        const rb = entry.running_balance ?? 0;
                        return (
                          <tr key={`stmt-${i}`} className={`border-b border-zinc-100 ${isBF ? 'bg-blue-50/40' : isPay ? 'bg-emerald-50/20' : ''}`} data-testid={`stmt-row-${i}`}>
                            <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{fmtDate(entry.date)}</td>
                            <td className={`px-4 py-2.5 ${isBF ? 'font-semibold text-zinc-800' : 'text-zinc-700'}`}>
                              {isBF && <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase border border-blue-200 bg-blue-100 text-blue-700 mr-2">B/F</span>}
                              {desc}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">{!isPay ? <span className="font-semibold">&euro;{entry.amount?.toFixed(2)}</span> : ''}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{isPay ? <>&euro;{entry.amount?.toFixed(2)}</> : ''}</td>
                            <td className={`px-4 py-2.5 text-right font-mono font-bold ${rb > 0 ? 'text-red-600' : rb < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>&euro;{rb.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300">
                        <td colSpan={2} className="px-4 py-2.5 text-right text-xs uppercase tracking-wider text-zinc-500">{t('total')}</td>
                        <td className="px-4 py-2.5 text-right font-mono">&euro;{s.total_charges?.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-700">&euro;{s.total_payments?.toFixed(2)}</td>
                        <td className={`px-4 py-2.5 text-right font-mono font-bold ${isPaid ? 'text-emerald-700' : 'text-red-700'}`}>&euro;{s.remaining_balance?.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ==================== ADD PAYMENT DIALOG ==================== */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('recordPayment')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={payForm.date} onChange={v => setPayForm({ ...payForm, date: v })} className="mt-1 font-mono" required data-testid="payment-date-input" />
              </div>
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0.01" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required data-testid="payment-amount-input" />
              </div>
              {/* Phase 220 — Payment method (cash | bank) and bank-account
                  picker. Required so the Finance income lands on the right
                  account for the bank-balance / VAT engine. */}
              <div>
                <Label>{t('paymentMethod') || 'Payment Method'}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="payment-method-toggle">
                  {[
                    { key: 'bank', label: t('paymentMethodBank') || 'Bank' },
                    { key: 'cash', label: t('paymentMethodCash') || 'Cash' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setPayForm({ ...payForm, payment_source: opt.key, bank_account_id: opt.key === 'bank' ? (payForm.bank_account_id || bankAccounts[0]?.account_id || '') : '' })}
                      className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${payForm.payment_source === opt.key ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`payment-method-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {payForm.payment_source === 'bank' && (
                <div>
                  <Label>{t('bankAccount') || 'Bank Account'} *</Label>
                  <select
                    value={payForm.bank_account_id}
                    onChange={e => setPayForm({ ...payForm, bank_account_id: e.target.value })}
                    className="mt-1 w-full border border-zinc-200 px-3 py-2 text-sm font-mono rounded-none bg-white"
                    required
                    data-testid="payment-bank-select"
                  >
                    <option value="">— {t('select') || 'Select'} —</option>
                    {bankAccounts.map(b => (
                      <option key={b.account_id} value={b.account_id}>{b.name} {b.bank_name ? `· ${b.bank_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid="payment-notes-input" />
              </div>
              {/* Phase 220 — Sync toggle (default ON). When OFF, the Finance
                  income is NOT created for this payment. */}
              <label className="flex items-start gap-2 px-3 py-2 border border-emerald-200 bg-emerald-50/40 cursor-pointer" data-testid="sync-to-finance-toggle">
                <input
                  type="checkbox"
                  checked={payForm.sync_to_finance}
                  onChange={e => setPayForm({ ...payForm, sync_to_finance: e.target.checked })}
                  className="mt-0.5"
                  data-testid="sync-to-finance-checkbox"
                />
                <div className="flex-1 leading-tight">
                  <div className="text-xs font-bold text-emerald-800">{t('syncToFinance') || 'Sync to Finance'}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">{t('syncToFinanceHint') || 'Auto-create a matching Finance income; updates and deletes cascade.'}</div>
                </div>
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-900 rounded-none" data-testid="save-payment-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ==================== ADD/EDIT INVOICE DIALOG ==================== */}
        <Dialog open={invDialogOpen} onOpenChange={(open) => { if (!open) { setInvDialogOpen(false); setEditingInvoice(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingInvoice ? t('editInvoice') : t('addInvoice')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveInvoice} className="space-y-4">
              {/* Phase 211 — Recurring vs Manual toggle (only for new invoices). */}
              {!editingInvoice && (
                <div className="grid grid-cols-2 gap-2" data-testid="invoice-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setInvMode('recurring')}
                    className={`p-3 border text-left transition-colors ${invMode === 'recurring' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                    data-testid="invoice-mode-recurring"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider">{t('recurring') || 'Recurring'}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{t('recurringDesc') || 'Auto monthly from building pricing'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvMode('manual')}
                    className={`p-3 border text-left transition-colors ${invMode === 'manual' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                    data-testid="invoice-mode-manual"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider">{t('manual') || 'Manual'}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{t('manualDesc') || 'One-time custom invoice'}</p>
                  </button>
                </div>
              )}

              {/* RECURRING summary (shown only when recurring is selected on a NEW invoice). */}
              {!editingInvoice && invMode === 'recurring' ? (
                <div className="border border-zinc-200 bg-zinc-50/60 p-3 space-y-1.5" data-testid="recurring-summary">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">{t('monthlyCharge') || 'Monthly Charge (ex VAT)'}</span>
                    <span className="font-mono font-semibold">€{(parseFloat(bld.cleaning_fee || 0)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">{t('vatPercent') || 'VAT %'}</span>
                    <span className="font-mono font-semibold">{(parseFloat(bld.cleaning_vat_rate ?? 19)).toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between text-xs border-t border-zinc-200 pt-1.5 mt-1.5">
                    <span className="font-semibold">{t('totalIncVat') || 'Total (incl. VAT)'}</span>
                    <span className="font-mono font-bold">€{(parseFloat(bld.cleaning_fee || 0) * (1 + parseFloat(bld.cleaning_vat_rate ?? 19) / 100)).toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-zinc-400 pt-2">
                    {bld.cleaning_charge_mode === 'fixed_monthly'
                      ? (t('recurringAlreadyOn') || 'Recurring is already enabled. Re-saving keeps the current pricing.')
                      : (t('recurringWillEnable') || 'Saving will switch this building to monthly auto-invoicing.')}
                  </p>
                  {parseFloat(bld.cleaning_fee || 0) <= 0 && (
                    <p className="text-[10px] text-red-600 pt-1">{t('setPricingFirst') || 'Set monthly cleaning charge in Edit Building first.'}</p>
                  )}
                </div>
              ) : (
              <>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={invForm.date} onChange={v => setInvForm({ ...invForm, date: v })} className="mt-1 font-mono" required data-testid="invoice-date-input" />
                {editingInvoice?.source === 'auto' && (
                  <p className="text-[10px] text-zinc-400 mt-1">{t('autoInvoice')} — date locked</p>
                )}
              </div>
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={invForm.amount}
                  onChange={e => setInvForm({ ...invForm, amount: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  required
                  data-testid="invoice-amount-input"
                />
              </div>
              <div>
                <Label>{t('invoiceDescription')}</Label>
                <Input
                  type="text"
                  value={invForm.description}
                  onChange={e => setInvForm({ ...invForm, description: e.target.value })}
                  className="mt-1 rounded-none"
                  data-testid="invoice-description-input"
                />
              </div>
              {/* Type selector — only for new or manual invoices */}
              {(!editingInvoice || editingInvoice.source === 'manual') && (
                <div>
                  <Label>{t('invoiceType')}</Label>
                  <Select value={invForm.invoice_type} onValueChange={v => setInvForm({ ...invForm, invoice_type: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cleaning">{t('cleaning')}</SelectItem>
                      <SelectItem value="extra">{t('extraWork')}</SelectItem>
                      <SelectItem value="other">{t('other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{t('notes')}</Label>
                <Textarea
                  value={invForm.notes}
                  onChange={e => setInvForm({ ...invForm, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  disabled={editingInvoice?.source === 'auto'}
                  data-testid="invoice-notes-input"
                />
              </div>
              </>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setInvDialogOpen(false); setEditingInvoice(null); }} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-900 rounded-none" data-testid="save-invoice-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ==================== DELETE PAYMENT CONFIRMATION ==================== */}
        <AlertDialog open={!!deletePayId} onOpenChange={() => setDeletePayId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePayment} className="bg-red-600 rounded-none" data-testid="confirm-delete-payment">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ==================== DELETE INVOICE CONFIRMATION ==================== */}
        <AlertDialog open={!!deleteInvTarget} onOpenChange={() => setDeleteInvTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteInvoice')}</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteInvTarget?.source === 'auto'
                  ? t('confirmDeleteAutoInvoice')
                  : t('confirmDeleteManualInvoice')
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 rounded-none" data-testid="confirm-delete-invoice">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== EDIT BUILDING DIALOG (Phase 209y) — available for all building types ====== */}
        <Dialog open={editBldgDialogOpen} onOpenChange={setEditBldgDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editBuilding')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditBldgSubmit} className="space-y-4">
              {/* Building Name & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t('buildingName')} *</Label>
                  <Input value={editBldgForm.name} onChange={e => setEditBldgForm({...editBldgForm, name: e.target.value})} required className="mt-1 rounded-none" data-testid="edit-bldg-name" />
                </div>
                <div>
                  <Label className="text-xs">{t('buildingType')}</Label>
                  <Select value={editBldgForm.building_type} onValueChange={v => setEditBldgForm({...editBldgForm, building_type: v})}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="edit-bldg-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="managed">{t('managed')}</SelectItem>
                      <SelectItem value="cleaning_only">{t('cleaning_only')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Structured Address */}
              <div className="space-y-3 border-t border-zinc-200 pt-3">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('address')}</p>
                <div>
                  <Label className="text-xs">{t('streetAddress')}</Label>
                  <Input value={editBldgForm.street_address} onChange={e => setEditBldgForm({...editBldgForm, street_address: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-street" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">{t('zipCode')}</Label>
                    <Input value={editBldgForm.zip_code} onChange={e => setEditBldgForm({...editBldgForm, zip_code: e.target.value})} className="mt-1 rounded-none font-mono" data-testid="edit-bldg-zip" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('city')}</Label>
                    <Input value={editBldgForm.city} onChange={e => setEditBldgForm({...editBldgForm, city: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-city" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('countryRegion')}</Label>
                    <Input value={editBldgForm.country} onChange={e => setEditBldgForm({...editBldgForm, country: e.target.value})} className="mt-1 rounded-none" data-testid="edit-bldg-country" />
                  </div>
                </div>
              </div>

              {/* Contact Person — for all building types */}
              <div className="space-y-3 border-t border-zinc-200 pt-3">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('contactPerson')}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('contactName') || t('name')}</Label>
                    <Input
                      value={(editBldgForm.managers?.[0]?.name) || ''}
                      onChange={e => {
                        const mgrs = [...(editBldgForm.managers || [])];
                        if (mgrs.length === 0) mgrs.push({ name: '', phone: '' });
                        mgrs[0] = { ...mgrs[0], name: e.target.value };
                        setEditBldgForm({...editBldgForm, managers: mgrs});
                      }}
                      placeholder="Maria Constantinou"
                      className="mt-1 rounded-none" data-testid="edit-bldg-contact-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('contactPhone') || t('phone')}</Label>
                    <Input
                      value={(editBldgForm.managers?.[0]?.phone) || ''}
                      onChange={e => {
                        const mgrs = [...(editBldgForm.managers || [])];
                        if (mgrs.length === 0) mgrs.push({ name: '', phone: '' });
                        mgrs[0] = { ...mgrs[0], phone: e.target.value };
                        setEditBldgForm({...editBldgForm, managers: mgrs});
                      }}
                      placeholder="+357 99123456"
                      pattern="^[+\d\s\-()]*$"
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-contact-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Phase 211 — Billing / Pricing section. Mirrors the one in
                  BuildingDetails so cleaning_only buildings landing on this
                  page can configure monthly charge / VAT / total + the
                  charge mode (per-cleaning vs fixed-monthly recurring). */}
              <div className="space-y-3 border-t border-zinc-200 pt-3">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('billingPricing') || 'Billing / Pricing'}</p>
                <div>
                  <Label className="text-xs">{t('chargeMode') || 'Charge Mode'}</Label>
                  <Select
                    value={editBldgForm.cleaning_charge_mode || 'per_cleaning'}
                    onValueChange={v => setEditBldgForm({...editBldgForm, cleaning_charge_mode: v})}
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="edit-bldg-charge-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_cleaning">{t('perCleaning') || 'Per Cleaning'}</SelectItem>
                      <SelectItem value="fixed_monthly">{t('fixedMonthly') || 'Fixed Monthly (Recurring)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">{t('monthlyCharge') || 'Monthly Charge (ex VAT)'}</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={editBldgForm.cleaning_fee ?? 0}
                      onChange={e => setEditBldgForm({...editBldgForm, cleaning_fee: parseFloat(e.target.value || '0')})}
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-cleaning-fee"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('vatPercent') || 'VAT %'}</Label>
                    <Input
                      type="number" step="0.01" min="0" max="100"
                      value={editBldgForm.cleaning_vat_rate ?? 19}
                      onChange={e => setEditBldgForm({...editBldgForm, cleaning_vat_rate: parseFloat(e.target.value || '0')})}
                      className="mt-1 rounded-none font-mono" data-testid="edit-bldg-cleaning-vat"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('totalIncVat') || 'Total (incl. VAT)'}</Label>
                    <Input
                      type="text" readOnly
                      value={`€${(parseFloat(editBldgForm.cleaning_fee || 0) * (1 + parseFloat(editBldgForm.cleaning_vat_rate || 0) / 100)).toFixed(2)}`}
                      className="mt-1 rounded-none font-mono bg-zinc-50 text-zinc-600" data-testid="edit-bldg-cleaning-total"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="border-t border-zinc-200 pt-3">
                <Label className="text-xs">{t('notes')}</Label>
                <Textarea value={editBldgForm.notes} onChange={e => setEditBldgForm({...editBldgForm, notes: e.target.value})} rows={3} className="mt-1 rounded-none" data-testid="edit-bldg-notes" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setEditBldgDialogOpen(false)} data-testid="edit-bldg-cancel">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="edit-bldg-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
